// =============================================================================
// Importar a planilha de ferramentas do Financeiro
//
// O arquivo é lido NO NAVEGADOR (lib/ferramentasPlanilha). Login e senha só são
// lidos para quem vê o cofre (a administração, migration 066), e vão à parte,
// para a API cifrar antes de gravar; para os outros, essas colunas não são
// lidas e nada delas sai deste computador.
//
// A planilha é texto livre: o centro de custo vem escrito de vários jeitos (e às
// vezes com o nome de uma pessoa no lugar), e a conta, às vezes, pelo nome. A
// tela sugere o casamento com o cadastro do Nexa e deixa o Financeiro corrigir
// antes de gravar — o relatório de limpeza é esta própria tela.
//
// Importar de novo é seguro: o que já veio da planilha (mesma linha) não é
// duplicado. A que ninguém editou no Nexa é atualizada com a leitura nova; a
// editada fica como está (migration 066). Entram também as abas Pendências e
// Novas dos CSVs, e a "Obs / Tendência" do Painel Financeiro.
// =============================================================================

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, buscarFerramentas, FalhaApi, importarAcessosFerramentas, importarFerramentas } from "../lib/api.ts";
import { moeda } from "../lib/formato.ts";
import {
  lerFerramentasXlsx,
  sugerirCategoria,
  sugerirCentro,
  type FerramentaPlanilha,
  type LeituraFerramentas,
} from "../lib/ferramentasPlanilha.ts";
import { Painel, Pilula, Vazio } from "../componentes/ui.tsx";
import { CLASSE_BOTAO_BARRA } from "../componentes/Grade.tsx";
import { dolar, ROTULO_STATUS_FERRAMENTA } from "../lib/ferramentas.ts";

type Cadastros = {
  centros: { id: string; codigo: string; nome: string }[];
  categorias: { id: string; codigo: string | null; nome: string; natureza: string }[];
};

export function ImportarFerramentas() {
  const qc = useQueryClient();
  const [leitura, setLeitura] = useState<LeituraFerramentas | null>(null);
  const [lendo, setLendo] = useState(false);
  const [erroLeitura, setErroLeitura] = useState<string | null>(null);
  const [mapaCc, setMapaCc] = useState<Record<string, string>>({});
  const [mapaConta, setMapaConta] = useState<Record<string, string>>({});
  const [resultado, setResultado] = useState<string | null>(null);
  const [guardarAcessos, setGuardarAcessos] = useState(true);

  const cad = useQuery({ queryKey: ["cadastros"], queryFn: () => api.get<Cadastros>("/api/cadastros") });
  const existentes = useQuery({ queryKey: ["ferramentas"], queryFn: buscarFerramentas });
  const comAcesso = !!existentes.data?.cofre.permitido && !!existentes.data.cofre.configurado;
  const refsExistentes = useMemo(
    () => new Set((existentes.data?.ferramentas ?? []).map((f) => f.origem_ref).filter(Boolean)),
    [existentes.data]);
  const centros = cad.data?.centros ?? [];
  const contas = (cad.data?.categorias ?? []).filter((c) => c.natureza !== "receita");

  // O valor de cada texto: a escolha da pessoa, senão a sugestão.
  const ccDe = (t: string) => (t in mapaCc ? mapaCc[t]! : sugerirCentro(t, centros)?.id ?? "");
  const contaDe = (t: string) => (t in mapaConta ? mapaConta[t]! : sugerirCategoria(t, contas)?.id ?? "");

  const distintos = (campo: "centro_custo_texto" | "conta_texto") => {
    const m = new Map<string, number>();
    for (const f of leitura?.ferramentas ?? []) m.set(f[campo], (m.get(f[campo]) ?? 0) + 1);
    return [...m].sort((a, b) => b[1] - a[1]);
  };
  const ccsPlanilha = distintos("centro_custo_texto");
  const contasPlanilha = distintos("conta_texto");

  const novas = (leitura?.ferramentas ?? []).filter((f) => !refsExistentes.has(f.origem_ref));
  const jaCadastradas = (leitura?.ferramentas.length ?? 0) - novas.length;
  const semCc = (leitura?.ferramentas ?? []).filter((f) => !ccDe(f.centro_custo_texto)).length;
  const semConta = (leitura?.ferramentas ?? []).filter((f) => !contaDe(f.conta_texto)).length;

  async function escolher(arquivo: File | undefined) {
    setResultado(null);
    setErroLeitura(null);
    setLeitura(null);
    setMapaCc({});
    setMapaConta({});
    if (!arquivo) return;
    setLendo(true);
    try {
      const l = await lerFerramentasXlsx(arquivo, comAcesso);
      if (l.ferramentas.length === 0) setErroLeitura(l.erros.join(" ") || "Nenhuma ferramenta encontrada no arquivo.");
      setLeitura(l);
    } catch (e) {
      setErroLeitura(`Não foi possível ler o arquivo: ${(e as Error).message}`);
    } finally {
      setLendo(false);
    }
  }

  const payload = (f: FerramentaPlanilha) => {
    const { aba: _a, centro_custo_texto, conta_texto, ...resto } = f;
    return { ...resto, centro_custo_id: ccDe(centro_custo_texto) || null, categoria_id: contaDe(conta_texto) || null };
  };

  const importar = useMutation({
    mutationFn: async () => {
      const r = await importarFerramentas({
        arquivo: leitura!.nomeArquivo, itens: leitura!.ferramentas.map(payload),
        pendencias: leitura!.pendencias, candidatas: leitura!.candidatas,
      });
      const a = comAcesso && guardarAcessos && leitura!.acessos.length ? await importarAcessosFerramentas(leitura!.acessos) : null;
      return { r, a };
    },
    onSuccess: ({ r, a }) => {
      setResultado([
        `${r.importadas} ferramenta(s) nova(s)`,
        r.atualizadas ? `${r.atualizadas} atualizada(s) com a leitura nova` : null,
        r.mantidas ? `${r.mantidas} mantida(s) como estavam, porque foram editadas no Nexa` : null,
        r.iguais ? `${r.iguais} sem mudança` : null,
        r.excluidas ? `${r.excluidas} pulada(s), porque foram excluídas no Nexa` : null,
        `${r.pendencias} pendência(s) e ${r.candidatas} linha(s) de Novas dos CSVs`,
        a ? `${a.gravados} login/senha guardado(s) no cofre${a.ja_tinham ? ` (${a.ja_tinham} já tinham e ficaram como estavam)` : ""}` : null,
      ].filter(Boolean).join("; ") + ".");
      void qc.invalidateQueries({ queryKey: ["ferramentas"] });
    },
  });

  const sel = "w-full max-w-[320px] rounded-lg border border-linha bg-superficie px-2.5 py-1.5 text-[12.5px]";

  return (
    <div className="pagina pb-20">
      <header className="pt-7 pb-5">
        <p className="text-[11px] font-semibold tracking-[0.11em] text-tinta-fraca uppercase">Ferramentas</p>
        <h1 className="mt-1 font-display text-[22px] leading-tight font-bold tracking-tight">Importar a planilha de ferramentas</h1>
        <p className="mt-1 max-w-3xl text-[14px] text-tinta-fraca">
          Baixe a planilha “Controle Ferramentas” do Drive em .xlsx (Arquivo → Fazer download → Microsoft Excel) e escolha o
          arquivo aqui. Entram todas as abas: Controle Geral, Cancelados, Pendências, Novas dos CSVs, a “Obs / Tendência” do Painel
          Financeiro e as Atenções críticas das Instruções.
          {comAcesso
            ? " Login e senha vão para o cofre, cifrados: só a administração vê."
            : " As colunas de login e senha não são lidas e não saem do seu computador: só a administração as importa."}
        </p>
      </header>

      <Painel>
        <label className="flex flex-wrap items-center gap-3">
          <span className="text-[13px] font-semibold">Arquivo .xlsx</span>
          <input type="file" accept=".xlsx" onChange={(e) => void escolher(e.target.files?.[0])} className="text-[13px]" />
          {lendo && <span className="text-[12.5px] text-tinta-fraca">Lendo…</span>}
        </label>
        {erroLeitura && <p className="mt-3 text-[13px] text-risco-tinta">{erroLeitura}</p>}
      </Painel>

      {resultado && (
        <p role="status" className="mt-4 rounded-lg border border-ok-linha bg-ok-bg px-3 py-2 text-[13px] text-ok-tinta">{resultado}</p>
      )}

      {leitura && leitura.ferramentas.length > 0 && (
        <>
          <div className="mt-4 grid gap-3 sm:grid-cols-4">
            <Resumo rotulo="Lidas" valor={leitura.ferramentas.length}
              apoio={`${leitura.ferramentas.filter((f) => f.aba === "controle").length} do Controle Geral · ${leitura.ferramentas.filter((f) => f.aba === "cancelados").length} de Cancelados`} />
            <Resumo rotulo="Novas para importar" valor={novas.length} apoio={jaCadastradas ? `${jaCadastradas} já estão no cadastro: as não editadas no Nexa são atualizadas` : "nenhuma já cadastrada"} />
            <Resumo rotulo="Sem centro de custo" valor={semCc} apoio="escolha abaixo, ou ficam sem CC" alerta={semCc > 0} />
            <Resumo rotulo="Sem conta contábil" valor={semConta} apoio="escolha abaixo, ou ficam sem conta" alerta={semConta > 0} />
          </div>
          <p className="mt-3 rounded-lg border border-linha bg-superficie-2 px-3 py-2 text-[12.5px] text-tinta-media">
            Também entram {leitura.pendencias.filter((p) => p.secao === "pendencias").length} pendência(s),{" "}
            {leitura.pendencias.filter((p) => p.secao === "atencoes_criticas").length} atenção(ões) crítica(s) e{" "}
            {leitura.candidatas.length} linha(s) de Novas dos CSVs. Do Painel Financeiro, {leitura.painel.casadas} ferramenta(s)
            ganham a “Obs / Tendência” pelo nome; as linhas do painel que são partes de uma ferramenta (ex.: “Canva — R$35”) não
            casam com nome nenhum e ficam de fora.
          </p>
          {leitura.ignoradas.length > 0 && (
            <p className="mt-2 rounded-lg border border-linha bg-superficie-2 px-3 py-2 text-[12.5px] text-tinta-media">
              🔒 Colunas não lidas, de propósito: {leitura.ignoradas.join(", ")}. Só a administração importa login e senha.
            </p>
          )}
          {comAcesso && (
            <label className="mt-2 flex items-center gap-2 rounded-lg border border-linha bg-superficie-2 px-3 py-2 text-[12.5px] text-tinta-media">
              <input type="checkbox" checked={guardarAcessos} onChange={(e) => setGuardarAcessos(e.target.checked)} />
              Guardar no cofre os {leitura.acessos.length} login(s) e senha(s) da planilha, cifrados. Onde o Nexa já tem um, fica o do Nexa.
            </label>
          )}

          <div className="mt-5 grid gap-4 lg:grid-cols-2">
            <Painel>
              <h2 className="text-[15px] font-bold">Centros de custo da planilha</h2>
              <p className="mt-0.5 text-[12px] text-tinta-fraca">O que está escrito na planilha e o centro de custo do Nexa em que ele entra.</p>
              <table className="mt-3 w-full text-[12.5px]">
                <tbody>
                  {ccsPlanilha.map(([t, n]) => (
                    <tr key={t} className="border-t border-linha">
                      <td className="py-1.5 pr-2">{t || <em className="text-tinta-fraca">vazio</em>} <span className="text-tinta-fraca">({n})</span></td>
                      <td className="py-1.5">
                        <select value={ccDe(t)} onChange={(e) => setMapaCc((m) => ({ ...m, [t]: e.target.value }))}
                          className={`${sel} ${ccDe(t) ? "" : "border-atencao-linha bg-atencao-bg"}`}>
                          <option value="">— sem centro de custo —</option>
                          {centros.map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.nome}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Painel>
            <Painel>
              <h2 className="text-[15px] font-bold">Contas contábeis da planilha</h2>
              <p className="mt-0.5 text-[12px] text-tinta-fraca">Casadas pelo código; sem código, pelo nome.</p>
              <table className="mt-3 w-full text-[12.5px]">
                <tbody>
                  {contasPlanilha.map(([t, n]) => (
                    <tr key={t} className="border-t border-linha">
                      <td className="py-1.5 pr-2">{t || <em className="text-tinta-fraca">vazio</em>} <span className="text-tinta-fraca">({n})</span></td>
                      <td className="py-1.5">
                        <select value={contaDe(t)} onChange={(e) => setMapaConta((m) => ({ ...m, [t]: e.target.value }))}
                          className={`${sel} ${contaDe(t) ? "" : "border-atencao-linha bg-atencao-bg"}`}>
                          <option value="">— sem conta —</option>
                          {contas.map((c) => <option key={c.id} value={c.id}>{c.codigo ? `${c.codigo} · ` : ""}{c.nome}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Painel>
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-[15px] font-bold">Prévia</h2>
            <button type="button" disabled={importar.isPending || cad.isPending} onClick={() => importar.mutate()}
              className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45">
              {importar.isPending ? "Importando…" : `Importar a planilha (${leitura.ferramentas.length} ferramentas)`}
            </button>
          </div>
          {importar.isError && (
            <p className="mt-2 rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">{(importar.error as FalhaApi).message}</p>
          )}
          <div className="thin-scroll mt-2 max-h-[60vh] overflow-auto rounded-lg border border-linha bg-superficie">
            <table className="w-full border-collapse text-[12.5px]">
              <thead className="sticky top-0 bg-superficie-2 text-left text-[11px] font-semibold tracking-wide text-tinta-fraca uppercase">
                <tr>
                  <th className="px-3 py-2">Ferramenta</th><th className="px-3 py-2">Status</th><th className="px-3 py-2 text-right">R$ Mensal</th>
                  <th className="px-3 py-2 text-right">R$ Anual</th><th className="px-3 py-2 text-right">USD</th>
                  <th className="px-3 py-2">Forma · Dia</th><th className="px-3 py-2">Centro de custo</th><th className="px-3 py-2">Conta</th>
                  <th className="px-3 py-2">Para conferir</th>
                </tr>
              </thead>
              <tbody>
                {leitura.ferramentas.map((f) => {
                  const ja = refsExistentes.has(f.origem_ref);
                  const ccId = ccDe(f.centro_custo_texto), contaId = contaDe(f.conta_texto);
                  return (
                    <tr key={f.origem_ref} className="border-t border-linha align-top">
                      <td className="px-3 py-1.5">
                        <span className="font-semibold">{f.nome}</span>
                        <span className="block text-[11px] text-tinta-fraca">{f.aba === "cancelados" ? "Cancelados" : "Controle Geral"}, linha {f.linha_planilha}</span>
                      </td>
                      <td className="px-3 py-1.5">
                        {ROTULO_STATUS_FERRAMENTA[f.status]}
                        {ja && <span className="ml-1.5"><Pilula tom="neutro">já cadastrada</Pilula></span>}
                      </td>
                      <td className="num px-3 py-1.5 text-right whitespace-nowrap">{f.valor_mensal_centavos ? moeda(f.valor_mensal_centavos) : f.texto_planilha.valor_mensal_centavos ?? ""}</td>
                      <td className="num px-3 py-1.5 text-right whitespace-nowrap">{f.valor_anual_centavos ? moeda(f.valor_anual_centavos) : f.texto_planilha.valor_anual_centavos ?? ""}</td>
                      <td className="num px-3 py-1.5 text-right whitespace-nowrap">{f.valor_usd_centavos ? dolar(f.valor_usd_centavos) : f.texto_planilha.valor_usd_centavos ?? ""}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap">
                        {f.forma_pagamento ?? ""}{f.cartao_final ? ` · ${f.cartao_final}` : ""}{f.dia_vencimento ? ` · ${f.dia_vencimento}` : ""}
                      </td>
                      <td className="px-3 py-1.5">{ccId ? centros.find((c) => c.id === ccId)?.nome : <span className="text-atencao-tinta">sem CC</span>}</td>
                      <td className="px-3 py-1.5">{contaId ? contas.find((c) => c.id === contaId)?.codigo : <span className="text-atencao-tinta">sem conta</span>}</td>
                      <td className="px-3 py-1.5 text-[11.5px] text-tinta-media">{f.avisos_importacao.join("; ") || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!leitura && !erroLeitura && !lendo && (
        <div className="mt-4">
          <Vazio titulo="Nenhum arquivo escolhido" dica="Depois de importar, as ferramentas aparecem na aba Ferramentas, onde cada uma pode ser corrigida." />
        </div>
      )}
      {leitura && <div className="mt-3 flex justify-end"><button type="button" className={CLASSE_BOTAO_BARRA} onClick={() => void escolher(undefined)}>Escolher outro arquivo</button></div>}
    </div>
  );
}

function Resumo({ rotulo, valor, apoio, alerta = false }: { rotulo: string; valor: number; apoio: string; alerta?: boolean }) {
  return (
    <div className={`rounded-xl border bg-superficie px-4 py-3 ${alerta ? "border-atencao-linha" : "border-linha"}`}>
      <p className="text-[11px] font-semibold tracking-wide text-tinta-fraca uppercase">{rotulo}</p>
      <p className="num mt-1 text-[22px] font-bold">{valor}</p>
      <p className="text-[11.5px] text-tinta-fraca">{apoio}</p>
    </div>
  );
}
